import { useCallback, useEffect, useRef, useState } from 'react'
import { createFileRoute, notFound, redirect, useRouter } from '@tanstack/react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faChevronDown, faSliders, faArrowDownWideShort, faTableCellsLarge, faList, faPlay } from '@fortawesome/free-solid-svg-icons'
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons'
import { Footer, HeaderMiddle, Navbar, TopBar } from '../components/SiteChrome'
import { WhatsAppChannelSection } from '../components/WhatsAppChannelSection'
import { AdNewsProofSection } from '../components/HomeNewsSection'
import { CategoryBuyingGuide } from '../components/CategoryBuyingGuide'
import {
  getProductCategoryName,
  getProductPrice,
  getProductPrimaryImage,
  getStorefrontConfig,
  HIDDEN_CATEGORY_SLUGS,
  listStorefrontCategories,
  listStorefrontProducts,
  placeholderImage,
  toExternalImage,
  toExternalImageSrcSet,
} from '../lib/dropify'
import type { StorefrontCategory, StorefrontProduct } from '../lib/dropify'
import { brandTitle, buildBreadcrumbSchema, buildCategoryFaq, buildFaqSchema, categoryOgImage, absoluteUrl, padMetaDescription, stripHtml } from '../lib/seo'
import type { FaqItem } from '../lib/seo'
import { getCategoryContent } from '../data/categoryContent'
import { CATEGORY_BUYING_GUIDES, getCategoryGuideSlugs } from '../data/categoryBuyingGuides'
import { BRANDS, getBrandLandingHref } from '../data/brands'
import { whatsAppCtaProps, track, trackSelectItem, trackViewItemList } from '../lib/analytics'
import { MOQ_BY_SLUG } from '../lib/pricing'
import { normalizeProductOrderingFacts } from '../lib/productOrdering'
import { listDashboardNewsPosts } from '../lib/news'
import { StorefrontUnavailable } from '../components/StorefrontUnavailable'
import { storefrontResponseHeaders } from '../lib/storefrontResponse'

type ProductSort = 'newest' | 'az' | 'za'
type CategorySearchParams = { page?: number }

// Same admin WhatsApp number as the rest of the storefront. A category page is a
// high-intent moment (visitor is browsing one product family), so we surface a
// category-specific "minta harga grosir" CTA here. Mirrors the /pusat-grosir
// pattern, which is the site's best-converting page.
const WA_NUMBER = '6285704011028'

export function waCategoryLink(name: string) {
  const message = `Halo Dropify, saya ingin membeli ${name} secara grosir. Produk atau model: ___. Kebutuhan: ___ lusin. Kota tujuan: ___. Mohon konfirmasi MOQ, stok, ketentuan varian, dan quotation.`
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

function waProductLink(name: string) {
  const message = `Halo Dropify, saya ingin memeriksa ${name}. Kebutuhan: ___. Kota tujuan: ___. Mohon kirim foto stok aktual dan quotation.`
  return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`
}

// Per-category note rendered above the product grid, for doubts that block the
// order but aren't visible from product cards. Facts must match the category's
// supplier LP. Only slugs with a verified note get an entry.
const CATEGORY_GRID_NOTE: Record<string, string> = {
  'bra-lingerie':
    'Sebagian besar model bisa campur ukuran dan warna dalam satu order. MOQ kategori bra 25 lusin, isi 12 pcs per lusin.',
}

const SORT_OPTIONS: Array<{ label: string; value: ProductSort }> = [
  { label: 'Terbaru', value: 'newest' },
  { label: 'A–Z', value: 'az' },
  { label: 'Z–A', value: 'za' },
]

const CATEGORY_PAGE_SIZE = 12
const CATEGORY_GUIDE_PAGE_SIZE = 100
const MASK_CATEGORY_SLUG = 'masker'
const MEDICAL_CATEGORY_SLUG = 'perlengkapan-medis'
const CATEGORY_HERO_IMAGE_TRANSFORM = { width: 1600, height: 420, fitIn: true, format: 'webp', quality: 75 } as const
const PRODUCT_GRID_IMAGE_TRANSFORM = { width: 320, height: 320, smart: true, format: 'webp', quality: 58 } as const
const PRODUCT_LIST_IMAGE_TRANSFORM = { width: 180, height: 180, smart: true, format: 'webp', quality: 58 } as const

function getProductSortParams(sort: ProductSort) {
  if (sort === 'az') return { sort_by: 'name', sort_dir: 'asc' as const }
  if (sort === 'za') return { sort_by: 'name', sort_dir: 'desc' as const }
  return { sort_by: 'published_at', sort_dir: 'desc' as const }
}

function createMaskCategory(categories: StorefrontCategory[]): StorefrontCategory {
  const medical = categories.find((item) => item.slug === MEDICAL_CATEGORY_SLUG)
  return {
    id: 'virtual-category-masker',
    name: 'Masker',
    slug: MASK_CATEGORY_SLUG,
    description: 'Masker COMET Theo tersedia per box, sedangkan Buff Masker Katun tersedia per lusin untuk pembelian grosir.',
    image_url: medical?.image_url ?? null,
    parent_id: medical?.id ?? null,
    sort_order: medical?.sort_order ?? 0,
    children: [],
  }
}

export function isMaskCategoryProduct(product: StorefrontProduct) {
  return /\bmasker\b/i.test(`${product.name} ${product.slug}`)
}

export function categoryPageHref(slug: string, page: number) {
  return page <= 1 ? `/kategori/${slug}` : `/kategori/${slug}?page=${page}`
}

async function listCategoryGuideProducts(slug: string) {
  const filter = slug === MASK_CATEGORY_SLUG ? { search: 'masker' } : { category: slug }
  const first = await listStorefrontProducts({
    ...filter,
    page: 1,
    per_page: CATEGORY_GUIDE_PAGE_SIZE,
    sort_by: 'name',
    sort_dir: 'asc',
  })
  const remaining = await Promise.all(
    Array.from({ length: Math.max(0, first.total_pages - 1) }, (_, index) =>
      listStorefrontProducts({
        ...filter,
        page: index + 2,
        per_page: CATEGORY_GUIDE_PAGE_SIZE,
        sort_by: 'name',
        sort_dir: 'asc',
      }),
    ),
  )
  const guideSlugs = getCategoryGuideSlugs(slug)
  return [first, ...remaining]
    .flatMap((page) => page.items)
    .filter((product) => guideSlugs.has(product.slug) && (slug !== MASK_CATEGORY_SLUG || isMaskCategoryProduct(product)))
}

async function listCategoryProducts(slug: string, page: number, sort: ProductSort) {
  if (slug !== MASK_CATEGORY_SLUG) {
    return listStorefrontProducts({
      category: slug,
      page,
      per_page: CATEGORY_PAGE_SIZE,
      ...getProductSortParams(sort),
    })
  }

  const response = await listStorefrontProducts({
    search: 'masker',
    page: 1,
    per_page: 100,
    ...getProductSortParams(sort),
  })
  const masks = response.items.filter(isMaskCategoryProduct).map(normalizeProductOrderingFacts)
  const start = (page - 1) * CATEGORY_PAGE_SIZE
  return {
    items: masks.slice(start, start + CATEGORY_PAGE_SIZE),
    total: masks.length,
    page,
    per_page: CATEGORY_PAGE_SIZE,
    total_pages: Math.max(1, Math.ceil(masks.length / CATEGORY_PAGE_SIZE)),
  }
}

// Hand-tuned meta for categories with real Search Console impressions, where
// the generic template underperforms on CTR. Keep claims in sync with the
// storefront catalog.
const CATEGORY_META_OVERRIDES: Record<string, { title: string; description: string }> = {
  'kaos-kaki': {
    title: 'Grosir Kaos Kaki Surabaya untuk Toko & Reseller',
    description:
      'Grosir kaos kaki Surabaya, minimum 50 lusin. Pilih model sekolah, bayi, ankle, dan muslimah untuk toko. Cek ukuran, stok, dan ongkir melalui WhatsApp.',
  },
}

const CATEGORY_HERO_OVERRIDES: Record<string, { title?: string; description: string }> = {
  'kaos-kaki': {
    title: 'Grosir kaos kaki Surabaya untuk toko dan reseller',
    description: 'Pilih model sekolah, bayi, ankle wanita, jempol muslimah, atau stocking untuk stok toko. Minimum kategori 50 lusin (600 pasang). Campuran model dan ukuran diperiksa sebelum quotation.',
  },
  'bra-lingerie': {
    title: 'Katalog bra, miniset, dan lingerie',
    description: 'Cari produk berdasarkan merek atau nomor model, lalu bandingkan ukuran, pilihan warna, dan MOQ sebelum meminta quotation.',
  },
  'cd-underwear': {
    title: 'Katalog CD dan underwear pria, wanita, serta anak',
    description: 'Bandingkan potongan, nomor model, ukuran, dan merek untuk menyusun stok toko sebelum meminta quotation.',
  },
  'piyama-oneset': {
    description: 'Pilih baju tidur anak, homedress, dan piyama dewasa untuk stok toko. Ukuran dan satuan pembelian mengikuti model.',
  },
}

export const Route = createFileRoute('/kategori/$slug')({
  beforeLoad: ({ params }) => {
    if (params.slug === 'perlengkapan-medis') {
      throw redirect({ href: '/perlengkapan-medis', statusCode: 301 })
    }
  },
  validateSearch: (search: Record<string, unknown>): CategorySearchParams => {
    const parsed = Number(search.page)
    return Number.isInteger(parsed) && parsed > 1 ? { page: parsed } : {}
  },
  loaderDeps: ({ search }) => ({ page: search.page ?? 1 }),
  loader: async ({ params, deps }) => {
    if (HIDDEN_CATEGORY_SLUGS.has(params.slug)) throw notFound()

    const categoryGuidePromise = CATEGORY_BUYING_GUIDES[params.slug] && deps.page === 1
      ? listCategoryGuideProducts(params.slug)
        .catch(() => [] as StorefrontProduct[])
      : Promise.resolve([] as StorefrontProduct[])

    const productsResultPromise = listCategoryProducts(params.slug, deps.page, 'newest')
      .then((response) => ({ response, failed: false }))
      .catch(() => ({
        failed: true,
        response: {
        items: [] as StorefrontProduct[],
        total: 0,
        page: deps.page,
        per_page: CATEGORY_PAGE_SIZE,
        total_pages: 0,
        },
      }))

    const [config, categoriesResult, categoryGuideProducts, newsPosts, productsResult] = await Promise.all([
      getStorefrontConfig().catch(() => null),
      listStorefrontCategories()
        .then((categories) => ({ categories, failed: false }))
        .catch(() => ({ categories: [] as StorefrontCategory[], failed: true })),
      categoryGuidePromise,
      params.slug === 'kaos-kaki' ? listDashboardNewsPosts().catch(() => []) : Promise.resolve([]),
      productsResultPromise,
    ])

    const baseCategories = categoriesResult.categories
    const category = params.slug === MASK_CATEGORY_SLUG
      ? createMaskCategory(baseCategories)
      : baseCategories.find((item) => item.slug === params.slug) ?? null

    if (!category && !categoriesResult.failed) throw notFound()

    if (!productsResult.failed && deps.page > Math.max(1, productsResult.response.total_pages)) throw notFound()

    const categories = baseCategories.some((item) => item.slug === MASK_CATEGORY_SLUG)
      ? baseCategories
      : [...baseCategories, createMaskCategory(baseCategories)]
    const productsResponse = productsResult.response
    const resolvedCategory = category && params.slug === MASK_CATEGORY_SLUG && productsResponse.items[0]
      ? { ...category, image_url: getProductPrimaryImage(productsResponse.items[0]) }
      : category

    return {
      routeSlug: params.slug,
      config,
      category: resolvedCategory,
      categories,
      products: productsResponse.items,
      productTotal: productsResult.failed ? null : productsResponse.total,
      productsFailed: productsResult.failed,
      currentPage: deps.page,
      totalPages: productsResponse.total_pages,
      categoryGuideProducts,
      newsPosts,
    }
  },
  headers: ({ loaderData }) => storefrontResponseHeaders(loaderData?.productsFailed ?? false),
  head: ({ loaderData, params }) => {
    const category = loaderData?.category
    const name = category?.name?.trim() || params.slug
    const override = CATEGORY_META_OVERRIDES[params.slug]
    const content = getCategoryContent(params.slug, name)
    // "Surabaya" in the title targets the commercial local queries the Ads
    // query report surfaced ("grosir kaos kaki", "pusat grosir ... surabaya").
    const currentPage = loaderData?.currentPage ?? 1
    const pageSuffix = currentPage > 1 ? ` Halaman ${currentPage}` : ''
    const title = brandTitle(`${override?.title ?? content.metaTitle ?? `Grosir ${name} Surabaya`}${pageSuffix}`)
    const rawDescription = stripHtml(category?.description)
    const description = override?.description ?? content.metaDescription ?? (rawDescription
      ? padMetaDescription(rawDescription, `Lihat katalog grosir ${name}. Kirim produk, jumlah, dan kota tujuan untuk meminta quotation.`)
      : `Lihat katalog grosir ${name} dari Dropify. Konfirmasikan varian, MOQ, stok, dan quotation melalui WhatsApp.`)

    return {
      meta: [
        { title },
        { name: 'description', content: description },
        { property: 'og:title', content: title },
        { property: 'og:description', content: description },
        { property: 'og:url', content: absoluteUrl(categoryPageHref(params.slug, currentPage)) },
        { property: 'og:image', content: categoryOgImage(category) },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: title },
        { name: 'twitter:description', content: description },
        { name: 'twitter:image', content: categoryOgImage(category) },
      ],
      links: [
        { rel: 'canonical', href: absoluteUrl(categoryPageHref(params.slug, currentPage)) },
      ],
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildBreadcrumbSchema([
            { name: 'Beranda', path: '/' },
            { name, path: `/kategori/${params.slug}` },
          ])),
        },
        {
          type: 'application/ld+json',
          children: JSON.stringify(buildFaqSchema(content.faq ?? buildCategoryFaq(name))),
        },
        // Mirror the visible first-page product grid so the category page
        // carries an ItemList like the brand hubs do.
        ...((loaderData?.products?.length ?? 0) > 0
          ? [{
              type: 'application/ld+json' as const,
              children: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'ItemList',
                name: `Grosir ${name} di Dropify`,
                itemListElement: (loaderData?.products ?? []).map((product, index) => ({
                  '@type': 'ListItem',
                  position: ((currentPage - 1) * CATEGORY_PAGE_SIZE) + index + 1,
                  item: {
                    '@type': 'Thing',
                    name: product.name,
                    image: getProductPrimaryImage(product),
                    url: absoluteUrl(`/produk/${product.slug || product.id}`),
                  },
                })),
              }),
            }]
          : []),
      ],
    }
  },
  component: CategoryPageRoute,
})

function CategoryPageRoute() {
  const { slug } = Route.useParams()
  const { page = 1 } = Route.useSearch()
  return <CategoryPage key={`${slug}-${page}`} />
}

function ProductCard({
  product,
  view,
  listName,
}: {
  product: StorefrontProduct
  view: 'grid' | 'list'
  listName: string
}) {
  const href = `/produk/${product.slug || product.id}`
  const image = getProductPrimaryImage(product, view === 'list' ? PRODUCT_LIST_IMAGE_TRANSFORM : PRODUCT_GRID_IMAGE_TRANSFORM)
  const categoryName = getProductCategoryName(product)
  const description = stripHtml(product.description)
    || `${product.name} tercantum dalam katalog Dropify. Hubungi admin untuk meminta detail produk dan quotation.`
  const selectItem = () =>
    trackSelectItem(
      { item_id: product.id, item_name: product.name, item_category: categoryName, price: getProductPrice(product) },
      listName,
    )
  // Per-card WA CTA — `product_card` converts on every catalog that has it
  // (GrosirLp, Oreo/Tally catalogs, /pusat-grosir cards); this template only
  // offered card → detail → CTA, and most browsers were lost on that hop.
  const chatLead = whatsAppCtaProps('category', {
      cta_placement: 'product_card',
      lp_category: listName,
      lead_item_name: product.name,
      items: [{ item_id: product.id, item_name: product.name, item_category: categoryName }],
    })

  if (view === 'list') {
    return (
      <div className="flex gap-4 bg-white rounded-xl border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all p-4 group">
        <a href={href} onClick={selectItem} className="flex-shrink-0 w-28 h-28 rounded-lg overflow-hidden bg-slate-100">
          <img src={image} alt={product.name} width="180" height="180" loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
        </a>
        <div className="flex flex-col justify-between flex-1 min-w-0">
          <div>
            <span className="text-xs text-blue-600 font-semibold uppercase tracking-wider">{categoryName}</span>
            <a href={href}>
              <h3 className="text-sm font-semibold text-slate-800 mt-0.5 hover:text-blue-600 transition-colors">{product.name}</h3>
            </a>
            <p className="text-xs text-slate-500 mt-1 line-clamp-2">{description}</p>
          </div>
          <div className="mt-3 flex items-center justify-end gap-3">
            <div className="flex items-center gap-2">
              <a
                href={waProductLink(product.name)}
                {...chatLead}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-4 py-3 bg-green-700 hover:bg-green-800 text-white text-xs font-semibold rounded-lg transition-colors w-fit"
              >
                <FontAwesomeIcon icon={faWhatsapp} className="w-3.5 h-3.5" />
                Periksa stok dan harga
              </a>
              <a
                href={href}
                className="inline-flex items-center gap-1.5 px-4 py-3 border border-slate-200 hover:border-blue-300 text-slate-700 hover:text-blue-600 text-xs font-semibold rounded-lg transition-colors w-fit"
              >
                Lihat detail
              </a>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="group bg-white rounded-xl overflow-hidden border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all duration-200">
      <a href={href} onClick={selectItem} className="block relative overflow-hidden bg-slate-100" style={{ height: 260 }}>
        <img src={image} alt={product.name} width="320" height="320" loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </a>
      <div className="p-4">
        <p className="text-xs text-blue-600 font-semibold uppercase tracking-wider mb-1">{categoryName}</p>
        <a href={href}>
          <h3 className="text-sm font-semibold text-slate-800 mb-2 leading-snug hover:text-blue-600 transition-colors">{product.name}</h3>
        </a>
        <div className="grid grid-cols-1 gap-2">
          <a
            href={waProductLink(product.name)}
            {...chatLead}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-3 bg-green-700 hover:bg-green-800 text-white text-xs font-semibold rounded-lg transition-colors w-full justify-center"
          >
            <FontAwesomeIcon icon={faWhatsapp} className="w-3.5 h-3.5" />
            Periksa stok dan harga
          </a>
          <a
            href={href}
            className="inline-flex items-center gap-1.5 px-3 py-2.5 border border-slate-200 hover:border-blue-300 text-slate-700 hover:text-blue-600 text-xs font-semibold rounded-lg transition-colors w-full justify-center"
          >
            Lihat detail
          </a>
        </div>
      </div>
    </div>
  )
}

function CategoryPage() {
  const router = useRouter()
  const { slug } = Route.useParams()
  const {
    routeSlug,
    category: loadedCategory,
    categories,
    products: initialProducts,
    config,
    productTotal: initialProductTotal,
    currentPage,
    totalPages,
    categoryGuideProducts,
    newsPosts,
    productsFailed,
  } = Route.useLoaderData()
  const loaderMatchesSlug = routeSlug === slug
  const category = loadedCategory?.slug === slug ? loadedCategory : categories.find((item) => item.slug === slug) ?? null

  const [allProducts, setAllProducts] = useState(loaderMatchesSlug ? initialProducts : [])
  const [productTotal, setProductTotal] = useState(loaderMatchesSlug ? initialProductTotal : 0)
  const [loadingProducts, setLoadingProducts] = useState(!loaderMatchesSlug)
  const [sort, setSort] = useState<ProductSort>('newest')
  const [view, setView] = useState<'grid' | 'list'>('grid')
  const [filterOpen, setFilterOpen] = useState(false)
  const [sortError, setSortError] = useState<ProductSort | null>(null)
  const [interactive, setInteractive] = useState(false)
  const sortRequest = useRef(0)
  const currentRoute = useRef('')
  currentRoute.current = `${slug}:${currentPage}`

  const listName = category?.name?.trim() || slug

  useEffect(() => {
    setInteractive(true)
    sortRequest.current += 1
    setSortError(null)
    setSort('newest')
    setFilterOpen(false)

    if (!loaderMatchesSlug) {
      setAllProducts([])
      setProductTotal(0)
      setLoadingProducts(true)
      return
    }

    setAllProducts(initialProducts)
    setProductTotal(initialProductTotal)
    setLoadingProducts(false)
    return () => { sortRequest.current += 1 }
  }, [slug, currentPage, loaderMatchesSlug, initialProducts, initialProductTotal])

  useEffect(() => {
    if (!loaderMatchesSlug || initialProducts.length === 0) return
    trackViewItemList(
      listName,
      initialProducts.map((product) => ({
        item_id: product.id,
        item_name: product.name,
        item_category: getProductCategoryName(product),
        price: getProductPrice(product),
      })),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, loaderMatchesSlug])

  const handleSortChange = (nextSort: ProductSort) => {
    if (nextSort === sort && sortError === null) return
    const request = ++sortRequest.current
    const route = currentRoute.current
    const isCurrent = () => request === sortRequest.current && route === currentRoute.current
    setSortError(null)
    setLoadingProducts(true)
    listCategoryProducts(slug, currentPage, nextSort)
      .then((res) => {
        if (!isCurrent()) return
        setAllProducts(res.items)
        setProductTotal(res.total)
        setSort(nextSort)
      })
      .catch(() => {
        if (isCurrent()) setSortError(nextSort)
      })
      .finally(() => { if (isCurrent()) setLoadingProducts(false) })
  }

  const displayedProducts = allProducts
  const guideProducts = categoryGuideProducts.length > 0 ? categoryGuideProducts : displayedProducts

  const categoryName = category?.name?.trim() || slug
  const heroOverride = CATEGORY_HERO_OVERRIDES[slug]
  const heroTitle = heroOverride?.title ?? categoryName
  const heroDescription = heroOverride?.description ?? (stripHtml(category?.description) || `Lihat pilihan grosir ${categoryName}, lalu kirim produk, jumlah, dan kota tujuan untuk meminta quotation.`)
  const heroImage = toExternalImage(category?.image_url, CATEGORY_HERO_IMAGE_TRANSFORM) || placeholderImage(categoryName, 1400, 200)
  const faqItems = getCategoryContent(slug, categoryName).faq ?? buildCategoryFaq(categoryName)

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar config={config} />
      <HeaderMiddle />
      <Navbar />

      <main>
      <div className="relative bg-slate-800 overflow-hidden" style={{ minHeight: 200 }}>
        <div className="absolute inset-0">
          <img src={heroImage}
            srcSet={toExternalImageSrcSet(category?.image_url, [640, 960, 1600].map((width) => ({ width, options: { ...CATEGORY_HERO_IMAGE_TRANSFORM, width } }))) || undefined}
            sizes="100vw" fetchPriority="high" alt={categoryName} width="1600" height="400" loading="eager" decoding="async" className="w-full h-full object-cover opacity-60" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-slate-900/30" />
        <div className="relative max-w-7xl mx-auto px-4 py-14 flex flex-col justify-center" style={{ minHeight: 200 }}>
          <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-3">
            <a href="/" className="hover:text-white transition-colors">Beranda</a>
            <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3" />
            <span className="text-white font-medium">{categoryName}</span>
          </nav>
          <h1 className="text-4xl font-black text-white tracking-tight mb-2">{heroTitle}</h1>
          <p className="text-slate-300 text-sm max-w-lg mb-4">{heroDescription}</p>
          {MOQ_BY_SLUG[slug] && (
            <div className="flex flex-wrap items-center gap-2 mb-5">
              {MOQ_BY_SLUG[slug] && (
                <span className="inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/25">
                  MOQ {MOQ_BY_SLUG[slug].lusin} lusin
                </span>
              )}
              <span className="inline-flex items-center rounded-full bg-white/15 px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/25">
                1 lusin = 12 {MOQ_BY_SLUG[slug]?.unit ?? 'pcs'}
              </span>
            </div>
          )}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 self-start">
            <a
              href={waCategoryLink(categoryName)}
              target="_blank"
              rel="noopener noreferrer"
              {...whatsAppCtaProps('category', { cta_placement: 'hero_primary', lp_category: categoryName })}
              className="inline-flex items-center justify-center gap-2 bg-green-700 hover:bg-green-800 text-white text-xs font-bold uppercase tracking-wider px-5 py-3 rounded-lg transition-colors shadow-lg"
            >
              <FontAwesomeIcon icon={faWhatsapp} className="w-4 h-4" />
              Minta Harga Grosir
            </a>
            <a
              href={slug === 'kaos-kaki' ? '#produk-kategori' : `/katalog#kategori-${slug}`}
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs font-bold uppercase tracking-wider px-5 py-3 rounded-lg ring-1 ring-white/25 transition-colors"
            >
              <FontAwesomeIcon icon={faPlay} className="w-3 h-3 text-blue-300" />
              {slug === 'kaos-kaki' ? 'Pilih model kaos kaki' : 'Lihat katalog'}
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex gap-8">
          <aside className="hidden lg:block w-56 flex-shrink-0">
            <div className="bg-white rounded-xl border border-slate-100 p-5 sticky top-4">
              <h2 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-4">Kategori Lainnya</h2>
              <ul className="space-y-1">
                {categories.map((item) => (
                  <li key={item.id}>
                    <a
                      href={`/kategori/${item.slug}`}
                      className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        item.slug === slug
                          ? 'bg-blue-500 text-white font-semibold'
                          : 'text-slate-600 hover:bg-blue-50 hover:text-blue-600'
                      }`}
                    >
                      {item.name}
                      {item.slug === slug && productTotal !== null && <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded-full">{productTotal}</span>}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <div id="produk-kategori" className="flex-1 min-w-0 scroll-mt-4">
            <h2 className="sr-only">Produk grosir {categoryName}</h2>
            {CATEGORY_GRID_NOTE[slug] && (
              <div className="mb-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm leading-relaxed text-slate-700">
                {CATEGORY_GRID_NOTE[slug]}
              </div>
            )}
            {currentPage === 1 && <CategoryBuyingGuide slug={slug} categoryName={categoryName} products={guideProducts} />}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6 bg-white border border-slate-100 rounded-xl px-4 py-3">
              <p className="text-sm text-slate-500">
                {productTotal === null ? 'Jumlah produk belum tersedia' : <>
                <span className="font-semibold text-slate-800">{displayedProducts.length}</span>
                {productTotal > displayedProducts.length ? ` dari ${productTotal}` : ''} produk dimuat
                </>}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setFilterOpen(!filterOpen)}
                  className="lg:hidden flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-600 hover:border-blue-300 transition-colors"
                >
                  <FontAwesomeIcon icon={faSliders} className="w-3.5 h-3.5" /> Filter
                </button>

                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                  <FontAwesomeIcon icon={faArrowDownWideShort} className="w-3.5 h-3.5" />
                  <select
                    aria-label="Urutkan produk"
                    value={sort}
                    onChange={(e) => handleSortChange(e.target.value as ProductSort)}
                    disabled={!interactive || loadingProducts || productsFailed}
                    className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:outline-none focus:border-blue-400 bg-white"
                  >
                    {SORT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setView('grid')}
                    className={`p-2.5 transition-colors ${view === 'grid' ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-slate-600'}`}
                    aria-label="Grid view"
                  >
                    <FontAwesomeIcon icon={faTableCellsLarge} className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setView('list')}
                    className={`p-1.5 transition-colors ${view === 'list' ? 'bg-blue-500 text-white' : 'text-slate-400 hover:text-slate-600'}`}
                    aria-label="List view"
                  >
                    <FontAwesomeIcon icon={faList} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {filterOpen && (
              <div className="lg:hidden bg-white border border-slate-100 rounded-xl p-4 mb-4 flex gap-2 flex-wrap">
                {categories.map((item) => (
                  <a
                    key={item.id}
                    href={`/kategori/${item.slug}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                      item.slug === slug
                        ? 'bg-blue-500 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-600'
                    }`}
                  >
                    {item.name}
                  </a>
                ))}
              </div>
            )}

            {productsFailed && <StorefrontUnavailable path={categoryPageHref(slug, currentPage)} category={listName} onRetry={() => router.invalidate()} />}
            {sortError && <div role="status" className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-slate-700">
              Urutan produk belum bisa diperbarui. Produk sebelumnya tetap ditampilkan.
              <button className="ml-2 min-h-11 px-3 font-semibold underline" onClick={() => handleSortChange(sortError)}>Coba urutkan lagi</button>
            </div>}
            {productsFailed ? null : loadingProducts && displayedProducts.length === 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="rounded-xl bg-slate-100 animate-pulse" style={{ height: 360 }} />
                ))}
              </div>
            ) : displayedProducts.length === 0 ? (
              <div className="text-center py-20 text-slate-400">
                <p className="text-lg font-semibold mb-1">Belum ada produk</p>
                <p className="text-sm">Tidak ada produk yang dapat ditampilkan untuk kategori dan filter ini. Ubah filter atau hubungi admin untuk meminta katalog.</p>
              </div>
            ) : view === 'grid' ? (
              <div data-product-catalog className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {displayedProducts.map((product) => <ProductCard key={product.id} product={product} view="grid" listName={listName} />)}
              </div>
            ) : (
              <div data-product-catalog className="flex flex-col gap-3">
                {displayedProducts.map((product) => <ProductCard key={product.id} product={product} view="list" listName={listName} />)}
              </div>
            )}

            {!productsFailed && !loadingProducts && totalPages > 1 && (
              <nav aria-label="Halaman produk" className="mt-5 flex items-center justify-center gap-3 text-sm">
                {currentPage > 1 && (
                  <a href={categoryPageHref(slug, currentPage - 1)} className="rounded-lg border border-slate-300 px-4 py-2 font-semibold text-slate-700 hover:border-blue-400 hover:text-blue-700">
                    Halaman sebelumnya
                  </a>
                )}
                <span className="text-slate-500">Halaman {currentPage} dari {totalPages}</span>
                {currentPage < totalPages && (
                  <a href={categoryPageHref(slug, currentPage + 1)} className="rounded-lg border border-slate-300 px-4 py-2 font-semibold text-slate-700 hover:border-blue-400 hover:text-blue-700">
                    Halaman berikutnya
                  </a>
                )}
              </nav>
            )}
          </div>
        </div>
      </div>

      <CategoryLeadBand name={categoryName} />

      <SupplierLpCallout slug={slug} />

      <CategorySeoSection slug={slug} name={categoryName} />

      <CategoryFaqSection categoryName={categoryName} faqItems={faqItems} />
      {slug === 'kaos-kaki' && <AdNewsProofSection posts={newsPosts} />}
      <WhatsAppChannelSection showSocksPromo={false} />
      </main>

      <Footer config={config} categories={categories} />
    </div>
  )
}

// Category-specific conversion band. Category pages get real browse traffic but
// almost no leads because, unlike /pusat-grosir, they never ask for the sale.
// This puts a high-intent "minta harga grosir <category>" WhatsApp CTA right
// after the product grid.
function CategoryLeadBand({ name }: { name: string }) {
  return (
    <section className="bg-gradient-to-br from-green-700 to-green-800">
      <div className="max-w-4xl mx-auto px-4 py-12 text-center">
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
          Minta quotation grosir {name}
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-sm sm:text-base text-green-50/90 leading-relaxed">
          Kirim nama produk, jumlah, dan kota tujuan kepada admin. Admin akan memeriksa stok serta menyiapkan quotation sebelum Anda memesan.
        </p>
        <a
          href={waCategoryLink(name)}
          target="_blank"
          rel="noopener noreferrer"
          {...whatsAppCtaProps('category', { cta_placement: 'category_band', lp_category: name })}
          className="mt-6 inline-flex items-center justify-center gap-2.5 rounded-lg bg-white px-7 py-4 text-sm font-bold text-green-800 shadow-lg transition-colors hover:bg-green-50"
        >
          <FontAwesomeIcon icon={faWhatsapp} className="w-5 h-5" />
          Minta quotation melalui WhatsApp
        </a>
        <p className="mt-3 text-xs text-green-100/70">
          Pemesanan resmi Dropify dilakukan melalui WhatsApp.
        </p>
      </div>
    </section>
  )
}

// Categories with a dedicated supplier landing page. Those LPs convert 2–4x
// better than this template (GA4, Jun–Aug 2026), so each category hands its
// warmest visitors over via a visible callout under the product grid.
const CATEGORY_SUPPLIER_LP: Record<string, { href: string; label: string }> = {
  'bra-lingerie': {
    href: '/supplier-bra-lingerie-surabaya',
    label: 'Pelajari MOQ, sample, dan proses suplai di halaman supplier bra dan lingerie Surabaya',
  },
  'cd-underwear': {
    href: '/grosir-underwear-surabaya',
    label: 'Pelajari MOQ dan cara pengadaan di halaman grosir underwear Surabaya',
  },
  'kaos-kaki': {
    href: '/grosir-kaos-kaki',
    label: 'Butuh suplai rutin? Lihat halaman grosir kaos kaki Dropify',
  },
  'piyama-oneset': {
    href: '/grosir-piyama-oneset',
    label: 'Mau kulakan rutin? Lihat halaman grosir piyama & oneset',
  },
  'handuk': {
    href: '/grosir-handuk',
    label: 'Butuh pemasok rutin? Lihat halaman grosir handuk Dropify',
  },
  'keset-karpet': {
    href: '/grosir-keset-karpet',
    label: 'Mau kulakan rutin? Lihat halaman grosir keset & karpet',
  },
  'manshet-sarung-tangan': {
    href: '/lp/manshet-sarung-tangan',
    label: 'Butuh suplai rutin? Lihat halaman grosir manshet & sarung tangan',
  },
  masker: {
    href: '/masker-comet',
    label: 'Bandingkan tipe dan kemasan di halaman grosir masker COMET',
  },
}

const CATEGORY_SEO_HEADING: Record<string, string> = {
  'kaos-kaki': 'Panduan memilih kaos kaki untuk stok toko',
  'bra-lingerie': 'Panduan membaca katalog bra, miniset, dan lingerie',
  'cd-underwear': 'Panduan membaca katalog CD dan underwear',
  handuk: 'Panduan memilih ukuran dan model handuk',
}

function SupplierLpCallout({ slug }: { slug: string }) {
  const lp = CATEGORY_SUPPLIER_LP[slug]
  if (!lp) return null
  return (
    <div className="max-w-7xl mx-auto px-4 pb-8">
      <a
        href={lp.href}
        onClick={() => track('click_internal_link', { link_url: lp.href, link_text: lp.label, link_location: 'category_supplier_lp_callout' })}
        className="flex items-center justify-between gap-4 rounded-xl border border-blue-100 bg-blue-50 px-5 py-4 text-sm font-semibold text-blue-700 transition-colors hover:border-blue-300 hover:bg-blue-100"
      >
        <span>{lp.label}</span>
        <FontAwesomeIcon icon={faChevronRight} className="w-3.5 h-3.5 flex-shrink-0" />
      </a>
    </div>
  )
}

// Keyword-rich, indexable descriptive block. Lives only on the organic
// /kategori/<slug> page. Copy is unique per category, sourced from
// data/categoryContent.ts.
function CategorySeoSection({ slug, name }: { slug: string; name: string }) {
  const content = getCategoryContent(slug, name)
  // Brands stocked in this category — internal links to the /merek pages so
  // brand-name searchers (a top on-site search pattern per GA4) find them.
  const categoryBrands = BRANDS.filter((brand) => brand.categorySlugs.includes(slug))
  return (
    <section className="bg-slate-50 border-t border-slate-100 py-14">
      <div className="max-w-3xl mx-auto px-4">
        <h2 className="text-2xl font-black tracking-tight text-slate-900 mb-4">{CATEGORY_SEO_HEADING[slug] ?? `Panduan grosir ${name}`}</h2>
        <div className="space-y-4 text-sm sm:text-base text-slate-600 leading-relaxed">
          <p>{content.intro}</p>
          <p>{content.detail}</p>
          {categoryBrands.length > 0 && (
            <p>
              Merek yang tersedia di kategori ini:{' '}
              {categoryBrands.map((brand, i) => (
                <span key={brand.slug}>
                  {i > 0 && ', '}
                  <a href={getBrandLandingHref(brand, slug)} className="font-semibold text-blue-600 hover:text-blue-700">
                    {brand.name}
                  </a>
                </span>
              ))}
              .
            </p>
          )}
        </div>
        {!CATEGORY_BUYING_GUIDES[slug] && content.types && content.types.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-bold tracking-wide text-slate-700 mb-3">Pilihan grosir {name} di Dropify</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-600">
              {content.types.map((type) => (
                <li key={type} className="flex items-start gap-2">
                  <FontAwesomeIcon icon={faChevronRight} className="w-3 h-3 mt-1 text-blue-500 flex-shrink-0" />
                  <span>{type}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  )
}

function CategoryFaqSection({ categoryName, faqItems }: { categoryName: string; faqItems: FaqItem[] }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  // Qualified FAQ engagement: visitor dwelled on the section ≥5s AND expanded
  // ≥2 distinct questions. Fires `faq_engaged` once — a strong intent signal,
  // mirroring the home-page FAQ instrumentation.
  const sectionRef = useRef<HTMLElement | null>(null)
  const dwelledRef = useRef(false)
  const openedRef = useRef<Set<number>>(new Set())
  const firedRef = useRef(false)

  const maybeFireEngaged = useCallback(() => {
    if (firedRef.current) return
    if (dwelledRef.current && openedRef.current.size >= 2) {
      firedRef.current = true
      track('faq_engaged', { faq_location: 'category', faq_open_count: openedRef.current.size })
    }
  }, [])

  useEffect(() => {
    const element = sectionRef.current
    if (!element) return
    let dwellTimer: number | undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible =
          entry.intersectionRatio >= 0.5 ||
          (entry.isIntersecting && entry.intersectionRect.height >= window.innerHeight * 0.6)
        if (visible && !dwelledRef.current) {
          if (dwellTimer === undefined) {
            dwellTimer = window.setTimeout(() => {
              dwelledRef.current = true
              maybeFireEngaged()
            }, 5000)
          }
        } else if (!visible && dwellTimer !== undefined) {
          window.clearTimeout(dwellTimer)
          dwellTimer = undefined
        }
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1] },
    )
    observer.observe(element)
    return () => {
      observer.disconnect()
      if (dwellTimer !== undefined) window.clearTimeout(dwellTimer)
    }
  }, [maybeFireEngaged])

  return (
    <section ref={sectionRef} className="bg-white border-t border-slate-100 py-14">
      <div className="max-w-3xl mx-auto px-4">
        <div className="mb-8">
          <h2 className="text-2xl font-black tracking-tight text-slate-900 mb-2">Pertanyaan seputar grosir {categoryName}</h2>
          <p className="text-sm text-slate-500">Kirim pertanyaan yang belum terjawab kepada admin melalui WhatsApp.</p>
        </div>
        <div className="space-y-2">
          {faqItems.map((item, i) => (
            <div key={i} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              <button
                type="button"
                onClick={() => {
                  if (openIndex !== i) {
                    track('faq_open', { faq_question: item.q, faq_location: 'category' })
                    openedRef.current.add(i)
                    maybeFireEngaged()
                  }
                  setOpenIndex(openIndex === i ? null : i)
                }}
                aria-expanded={openIndex === i}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left text-sm font-semibold text-slate-800 hover:text-blue-600 transition-colors"
              >
                <span>{item.q}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${openIndex === i ? 'rotate-180 text-blue-600' : 'text-slate-400'}`}
                />
              </button>
              {openIndex === i && (
                <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
        <p className="mt-6 text-sm leading-relaxed text-slate-600">
          Pesanan pertama dan ingin memeriksa kiriman sebelum transfer?{' '}
          <a href="/jaminan-ekspedisi" className="font-semibold text-blue-700 underline underline-offset-2 hover:text-blue-900">Baca panduan verifikasi lewat ekspedisi pilihan</a>.
        </p>
      </div>
    </section>
  )
}
